/* ══════════════════════════════════════════════════════════════════════════════
 * consent-bridge — relais AppConsent (SFBX) → API Customer Privacy de Shopify
 *
 * POURQUOI CE FICHIER EXISTE
 * La CMP du site est AppConsent (SFBX), en IAB TCF v2. Elle n'est ni une app
 * Shopify, ni un app embed, ni du code de ce thème : elle est injectée par GTM
 * (`cdn.appconsent.io/loader.js`). Elle alimente correctement Google Consent
 * Mode v2 — mais elle n'appelle JAMAIS `Shopify.customerPrivacy.setTrackingConsent()`.
 *
 * Mesuré le 2026-09-22, après un « Tout Accepter » complet :
 *
 *   currentVisitorConsent       { marketing:"", analytics:"", preferences:"", sale_of_data:"" }
 *   analyticsProcessingAllowed  false
 *
 * Pour Shopify, aucun visiteur n'a donc jamais consenti. Les Web Pixels sont
 * restreints en conséquence — dont celui de Stape, seule source possible de
 * `checkout_completed`, donc de `purchase`. Aucune conversion ne remonte.
 *
 * Ce fichier ajoute le seul appel manquant. Il ne décide de rien : il traduit
 * l'état de la CMP, il ne le fabrique pas.
 *
 * POURQUOI DANS LE THÈME ET PAS DANS GTM
 * Ce pont conditionne le fonctionnement des Web Pixels, donc l'existence même
 * des events de checkout. C'est de l'infrastructure, pas de la configuration
 * marketing : il n'a pas sa place dans un conteneur piloté par un tiers, où un
 * tag peut cesser de se déclencher sans que personne ne le voie.
 *
 * CE QU'IL NE FAUT PAS FAIRE
 *   - Ne pas déplacer cette logique dans un Web Pixel / événement client
 *     personnalisé : le pixel est EN AVAL de la porte de consentement, c'est
 *     l'élément bloqué, pas le bloqueur. Sa sandbox n'expose d'ailleurs
 *     `customerPrivacy` qu'en lecture.
 *   - Ne pas chercher à le poser sur le checkout. `setTrackingConsent()` persiste
 *     le choix côté serveur dans le cookie `_tracking_consent` (HttpOnly), que le
 *     checkout et la sandbox relisent ensuite. Le storefront suffit, et le
 *     visiteur y passe forcément avant de payer.
 *
 * DEBUG : `?ax_debug=1` sur n'importe quelle URL logge chaque synchronisation.
 * Attention en recette : le cookie `_tracking_consent` étant HttpOnly, on ne peut
 * pas le purger en JS. Tester en navigation privée, sinon on relit un état ancien.
 * ═════════════════════════════════════════════════════════════════════════════ */

(() => {
  const DEBUG = new URLSearchParams(window.location.search).has('ax_debug');

  /**
   * Purposes IAB TCF v2 → signaux Shopify.
   *
   * Établi en mesurant ce qu'AppConsent accorde réellement : un « Tout Accepter »
   * donne les purposes 1, 2, 3, 4, 7, 8, 9, 10. Les purposes 5 et 6
   * (personnalisation de contenu) restent refusés EN TOUTES CIRCONSTANCES.
   * Les mapper ferait que `preferences` ne passerait jamais à true — piège
   * classique quand on complète la table « par symétrie ».
   *
   * Ce mapping est un défaut raisonnable, pas un arbitrage juridique.
   */
  const MAP = {
    analytics: [8, 9], // mesure de contenu + statistiques d'audience
    marketing: [2, 3, 4, 7], // sélection, profils et mesure publicitaires
    preferences: [1] // stockage / accès sur l'appareil
  };

  const POLL_INTERVAL = 200;
  const POLL_MAX_TRIES = 50; // 10 s

  // Signature du dernier état transmis. `setTrackingConsent` déclenche une
  // mutation serveur : inutile de la rejouer à l'identique à chaque event TCF.
  let lastSent = null;

  const log = (...args) => {
    if (DEBUG) {
      console.log('%c[consent]%c', 'color:#0ea5e9;font-weight:700', '', ...args);
    }
  };

  const push = (payload) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  /**
   * Un signal Shopify n'est accordé que si TOUS ses purposes le sont — par
   * consentement OU par intérêt légitime.
   *
   * Le TCF permet d'établir une finalité sur l'une ou l'autre base légale, et
   * AppConsent place les finalités de mesure (7 à 10) sur l'intérêt légitime.
   * Ne lire que `consents` laissait donc `analytics` à false en permanence pour
   * les visiteurs concernés : leurs purposes 8 et 9 n'apparaissent pas dans
   * `consents`, alors qu'ils sont accordés dans `legitimateInterests`.
   *
   * C'est sans danger pour le refus : vérifié le 2026-09-25, un « Tout refuser »
   * dans AppConsent vide LES DEUX tableaux, `consents` comme
   * `legitimateInterests`. L'opposition du visiteur est donc bien respectée.
   */
  const allGranted = (purpose, purposes) => {
    const consents = purpose?.consents || {};
    const legitimateInterests = purpose?.legitimateInterests || {};

    return purposes.every((id) => Boolean(consents[id] || legitimateInterests[id]));
  };

  const apply = (tcData) => {
    const purpose = tcData?.purpose;

    const consent = {
      analytics: allGranted(purpose, MAP.analytics),
      marketing: allGranted(purpose, MAP.marketing),
      preferences: allGranted(purpose, MAP.preferences)
    };

    consent.sale_of_data = consent.marketing;

    const signature = JSON.stringify(consent);

    if (signature === lastSent) {
      log('état inchangé, rien à transmettre', consent);

      return;
    }

    lastSent = signature;

    try {
      window.Shopify.customerPrivacy.setTrackingConsent(consent, () => {
        log('transmis à Shopify', consent);
        push({ event: 'shopify_consent_synced', shopify_consent: consent });
      });
    } catch (error) {
      // On réarme : un échec ne doit pas empêcher la prochaine tentative.
      lastSent = null;
      log('échec', error);
      push({ event: 'shopify_consent_error', shopify_consent_error: String(error) });
    }
  };

  /**
   * `addEventListener` de l'API TCF rejoue l'état courant dès l'abonnement, puis
   * notifie chaque changement d'avis. Un seul abonnement couvre donc le choix
   * déjà stocké, le choix qui vient d'être fait, et les retours par le
   * « Privacy center » en pied de page — sans rechargement.
   */
  const subscribe = () => {
    window.__tcfapi('addEventListener', 2, (tcData, success) => {
      if (!success || !tcData) {
        return;
      }

      if (tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'useractioncomplete') {
        apply(tcData);
      }
    });

    log('abonné à l’API TCF');
  };

  // AppConsent étant injectée par GTM, son API n'existe pas au chargement de ce
  // fichier. On l'attend plutôt que de supposer un ordre qui n'est pas garanti.
  const waitFor = (test, done, tries = 0) => {
    if (test()) {
      done();

      return;
    }

    if (tries >= POLL_MAX_TRIES) {
      log('abandon : prérequis absent après', (POLL_MAX_TRIES * POLL_INTERVAL) / 1000, 's');

      return;
    }

    setTimeout(() => waitFor(test, done, tries + 1), POLL_INTERVAL);
  };

  const privacyApiReady = () => Boolean(window.Shopify?.customerPrivacy?.setTrackingConsent);

  const withPrivacyApi = (next) => {
    if (privacyApiReady()) {
      next();

      return;
    }

    // Shopify ne charge l'API de consentement qu'à la demande.
    if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error) {
          log('loadFeatures a échoué', error);

          return;
        }

        next();
      });

      return;
    }

    waitFor(privacyApiReady, next);
  };

  withPrivacyApi(() => {
    waitFor(() => typeof window.__tcfapi === 'function', subscribe);
  });
})();
